import { useState } from "react";
import { Lock, Pencil, Plus, Trash2, UserRoundCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog, EmptyState, PageHeader, RowsSkeleton, Tag } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function Roles() {
  const { me, can } = useAuth();
  const manage = can("roles:manage");
  const { data, loading, reload } = useFetch("/t/roles");
  const perms = useFetch("/t/permissions");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const labels = Object.fromEntries((perms.data || []).map((p) => [p.code, p.label]));
  const options = (perms.data || []).map((p) => [p.code, p.label, !me.permissions.includes(p.code)]);
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Rôles et permissions" subtitle="Les permissions liées à un module désactivé sont automatiquement inopérantes."
        actions={manage && <Button onClick={() => setEdit({ permissions: [] })} data-testid="roles-create-button"><Plus className="h-4 w-4 mr-2" />Nouveau rôle</Button>} />
      {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={UserRoundCog} title="Aucun rôle" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {data.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5" data-testid={`role-card-${r.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold font-heading">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.members_count} utilisateur(s) {r.is_system && "· prédéfini"}</p>
                </div>
                {manage && r.code !== "admin" && (
                  <div className="flex">
                    <Button variant="ghost" size="sm" onClick={() => setEdit(r)} data-testid={`role-edit-${r.code}`}><Pencil className="h-4 w-4" /></Button>
                    {!r.is_system && <Button variant="ghost" size="sm" onClick={() => setDel(r)} data-testid={`role-delete-${r.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                  </div>
                )}
                {r.code === "admin" && <Lock className="h-4 w-4 text-slate-400" />}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4 min-w-0 [&>*]:max-w-full [&>*]:whitespace-normal [&>*]:break-words">{r.permissions.map((p) => <Tag key={p} tone={labels[p] ? "sky" : "gray"}>{labels[p] || `${p} (module inactif)`}</Tag>)}</div>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} wide testid="role-dialog" title={edit?.id ? `Modifier « ${edit.name} »` : "Nouveau rôle"}
        description="Vous ne pouvez accorder que des permissions dont vous disposez vous-même." initial={edit || {}}
        fields={[{ key: "name", label: "Nom du rôle", required: true, full: true }, { key: "permissions", label: "Permissions", type: "checklist", options }]}
        onSubmit={async (v) => {
          const body = { name: v.name, permissions: (v.permissions || []).filter((p) => labels[p]) };
          if (edit.id) await api.put(`/t/roles/${edit.id}`, body);
          else await api.post("/t/roles", body);
          toast.success("Rôle enregistré");
          reload();
        }} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive confirmLabel="Supprimer" title="Supprimer ce rôle ?" description={`Le rôle « ${del?.name} » sera supprimé.`}
        onConfirm={async () => { try { await api.delete(`/t/roles/${del.id}`); toast.success("Rôle supprimé"); setDel(null); reload(); } catch (e) { toast.error(formatApiError(e)); } }} />
    </div>
  );
}
